import { FC, useContext, useEffect, useState } from "react";
import { Box, Container, Divider, Grid, useMediaQuery, useTheme } from "@material-ui/core";
import { useHistory, useParams } from "react-router";

import useStyles from "../Styles/LearnStyles";
import AdminHeader from "../SubComponents/AdminHeader";
import Loader from "../../helpers/Loader";
import { filterQuizzesByPool } from "../../store/Learn2/LearnFunction";
import LearnContext, { useLearnContext } from "../../store/Learn2/LearnContext";
import { Question, QuestionDetail, Quiz, StartQuizType } from "../../store/Learn2/learn.interface";
import Footer from "../SubComponents/Footer";
import LearnSidebar from "./LearnSidebar";
import LearnVideo from "./LearnVideo";
import LearnQCM from "./LearnQCM";
import AlertContext from "../../store/Alert/AlertContext";
import HorizontalNonLinearStepper from "../ConnectWallet/WalletStepperNew";

interface Params {
    id: string;
}

const LearnPage: FC = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const params: Params  = useParams();
    const isActive = useMediaQuery(theme.breakpoints.down("sm"));
    const isActiveMD = useMediaQuery(theme.breakpoints.down("md"));

    const { quizzes, activeQuizzes, getPoolQuizzes, getPoolQuizQuestion, nextQuestion, answerQuizQuestion, quizIndex, updateQuizIndex, setQuizIndex, getAuthUserPoolQuiz, quizTracker, clearPreviousAttempt } = useLearnContext();
    const { setAlert } = useContext<any>(AlertContext);

    const [poolId, setPoolId] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [poolQuizzes, setPoolQuizzes] = useState<Quiz[]>([]);
    const [questionIndex, setQuestionIndex] = useState<number>(-1);
    const [currentQuiz, setCurrentQuiz] = useState<Quiz | undefined>();
    const [currentQuestion, setCurrentQuestion] = useState<QuestionDetail | undefined>();
    const [currentActiveQuizzes, setCurrentActiveQuiz] = useState<any>(null);
    const [hasFailed, setFailed] = useState<boolean>(false);
    const [progress, setProgress] = useState<boolean>(false);
    const [resume, setResume] = useState<boolean>(false);
    const [restart, setRestart] = useState<boolean>(false);

    useEffect(() => {
        getAuthUserPoolQuiz();
        setPoolId(params.id);
        setQuizIndex(-1);
    }, []);

    useEffect(() => {
        if (poolId && poolQuizzes.length === 0) {
            let poolQuizzes = filterQuizzesByPool(quizzes!, poolId);
            if (poolQuizzes.length === 0) getPoolQuizzes(poolId);
            else {
                setPoolQuizzes(poolQuizzes);
                // Check if current pool has active quizzes
                if (activeQuizzes !== null) {
                    let currentPoolQuizActive = activeQuizzes.filter((active: any) => {
                        return active.poolId === params.id
                    });
                    if (currentPoolQuizActive && currentPoolQuizActive.length > 0) {
                        setCurrentActiveQuiz(currentPoolQuizActive[0]);
                    } else {
                        if (quizIndex === -1 || (quizTracker && quizTracker.pool_id === poolId && quizTracker.isSuccessful)) {
                            updateQuizIndex();
                            setIsLoading(false);
                        }
                    }
                } else {
                    getAuthUserPoolQuiz();
                }
            }
        }
    }, [poolId, quizzes, activeQuizzes, quizTracker]);

    useEffect(() => {
        if (currentActiveQuizzes) {
            // Check in current active quizzes, if there is a quiz that the user has failed - number of attempts = 3 and last attempt has failed
            let hasFailedByAttemps = currentActiveQuizzes.quizzes.filter((active: any) => {
                return active.numberOfAttempts === 3 && active.lastAttempt.isSuccessful === false
            })
            if (hasFailedByAttemps && hasFailedByAttemps.length > 0) {
                setFailed(true);
            } else {
                // Find out the current active quiz
                let currentActiveQuiz = currentActiveQuizzes.quizzes.find((active: any) => {
                    return active.numberOfAttempts <= 3 && active.lastAttempt?.isSuccessful !== true
                });
                if (currentActiveQuiz) {
                    // This is the active quiz
                    let nextQuiz = findNextQuiz([currentActiveQuiz.quizId], true);
                    setQuizIndex(nextQuiz.orderNumber - 1);
                } else {
                    // This is the active quiz that is already successful
                    let activeQuizzesId = currentActiveQuizzes.quizzes.map((quiz: any) => {
                        return quiz.quizId
                    })
                    let nextQuiz = findNextQuiz(activeQuizzesId, false);
                    if (nextQuiz) setQuizIndex(nextQuiz.orderNumber - 1);
                    // If not next quiz, this means that user has successfully completed all the quizzes
                    else history.push(`/learn-result/${poolId}`);
                }
            }
        }
    }, [currentActiveQuizzes]);
    
    useEffect(() => {
        // Do nothing if quizIndex is -1
        if (quizIndex > -1 || progress) {
            let currentQuiz = poolQuizzes.filter((quiz: Quiz) => {
                return quiz.orderNumber === quizIndex + 1
            });
            // TODO: Handle !currentQuiz
            setCurrentQuiz(currentQuiz[0]);
        }
    }, [quizIndex]);

    useEffect(() => {
        // Ensure resume, reset and currentQuestion are null are set to false
        setResume(false);
        setRestart(false);
        if (!currentQuiz) return;
        if (currentQuestion) setCurrentQuestion(undefined);
        if (currentQuiz && !currentActiveQuizzes) {
            // No active quizzes, user taking the quiz for 
            // the first time
            setQuestionIndex(0);
            setIsLoading(false);
        } else {
            // Current quiz was in progress, check if the 
            // quiz has expired: restart quiz, if last question answered
            // set the questionIndex to next question, and no last answered question, 
            // resume the quiz at beggining.
            let currentQuizActive = currentActiveQuizzes.quizzes.find((active: any) => {
                return active.quizId === currentQuiz.id;
            })
            if (currentQuizActive) {
                if (currentQuizActive.lastAttempt.isSuccessful === false) {
                    clearPreviousAttempt();
                    setQuestionIndex(0);
                    setRestart(true);
                } else {
                    let startTime = Math.floor(new Date(currentQuizActive.lastAttempt.start_time).getTime() / 1000);
                    let expectedEndTime = startTime + currentQuiz.timeLimitInSeconds;
                    let currentTime = Math.floor(Date.now() / 1000);
                    if (currentTime > expectedEndTime) {
                        // Quiz expired, restart quiz but fail if this was the third attemp
                        if (currentQuizActive.numberOfAttempts === 3) {
                            setAlert('Oops! Looks like your previous quiz session has timed out. Maximum number of attempts used.', 'error');
                            setFailed(true);
                        } else {
                            clearPreviousAttempt();
                            setQuestionIndex(0);
                            setRestart(true);
                        }
                    } else {
                        // check last answered question
                        if (currentQuizActive.lastAttempt.last_answered_question_id === null) {
                            setQuestionIndex(0);
                            setResume(true);
                        } else {
                            let lastAnsweredQuestion = currentQuiz.question.find((q: any) => {
                                return q.id === currentQuizActive.lastAttempt.last_answered_question_id
                            });
                            if (lastAnsweredQuestion) {
                                let lastAnsweredQuestionOrderNumber = lastAnsweredQuestion.orderNumber;
                                let totalQuestions = currentQuiz.question.length;
                                if (lastAnsweredQuestionOrderNumber === totalQuestions) {
                                    setQuestionIndex(0);
                                    setRestart(true);
                                } else {
                                    setQuestionIndex(lastAnsweredQuestionOrderNumber);
                                    setResume(true);
                                }
                            } else {
                                // TODO: Handle this case - Not sure if its possible.
                            }
                        }
                    }
                }
                setIsLoading(false);
            } else {
                // This quiz is not active
                setQuestionIndex(0);
                setIsLoading(false);
            }

        }
    }, [currentQuiz])

    useEffect(() => {
        if (nextQuestion && nextQuestion.questionDetail) {
            setCurrentQuestion(nextQuestion.questionDetail);
        }
    }, [nextQuestion]);

    useEffect(() => {
        if (hasFailed) {
            history.push(`/learn-result-fail`);
        }
    }, [hasFailed]);

    const findNextQuiz = (successQuiz: any[], match: boolean) => {
        let remainingQuizzes = poolQuizzes.filter((quiz: any) => {
            if (match) return successQuiz.includes(quiz.id)
            else return !successQuiz.includes(quiz.id)
        })
        return remainingQuizzes[0];
    }

    const submitAnswer = (answerId: string) => {
        // Increase question or quiz index
        answerQuizQuestion(poolId!, currentQuiz?.id!, currentQuestion?.id!, answerId)
    }

    // This is basically used for get first quiestion 
    // const getNextQuestion = () => {
    //     let nextQuestion = currentQuiz?.question.filter((question: Question) => question.orderNumber === questionIndex + 1);
    //     if (nextQuestion && nextQuestion?.length > 0) {
    //         getPoolQuizQuestion(poolId, currentQuiz?.id, nextQuestion[0].id);
    //     } else {
    //         // Show alert
    //         // Handle this
    //     }
    // }

    const getNextQuestion = (options: {
        startQuizType: StartQuizType
    }) => {
        const { startQuizType} = options
        console.log(options)
        let nextQuestion = currentQuiz?.question.filter((question: Question) => question.orderNumber === questionIndex + 1);
        if (nextQuestion && nextQuestion?.length > 0) {
            getPoolQuizQuestion({
                poolId, 
                quizId: currentQuiz?.id,
                questionId: nextQuestion[0].id,
                startQuizType
            });
        } else {
            // Show alert
            // Handle this
        }
    }

    return (
        <>
            <AdminHeader isProject={true} />
            <HorizontalNonLinearStepper
                projectChangeEnabled={false}
                disableModals={false}
            />
            <Box mt={3}>
                <Divider />
            </Box>
            {
                isLoading
                ?
                    <Box my={10}>
                        <Loader />
                    </Box>
                :
                    <>
                        <Box className={classes.learnpages} component="section">
                            <Container fixed>
                                <Grid container justifyContent="center">
                                    <Grid
                                        item
                                        lg={10}
                                        md={9}
                                        sm={12}
                                        xs={12}
                                        className="border-right"
                                    >   
                                        {
                                            quizIndex < 0
                                            ? <Loader />
                                            : 
                                            currentQuiz
                                            ?
                                            currentQuestion
                                            ? <LearnQCM title={poolQuizzes[quizIndex]?.title} question={currentQuestion} submitAnswer={(answerId) => submitAnswer(answerId)} />
                                            : <LearnVideo quiz={currentQuiz} isActive={isActive} isActiveMD={isActiveMD} quizIndex={quizIndex} getNextQuestion={getNextQuestion} resume={resume} restart={restart} />
                                            : <Loader />
                                        }
                                    </Grid>
                                    <Grid 
                                        item 
                                        lg={2} 
                                        md={3} 
                                        sm={12} 
                                        xs={12}
                                    >
                                        <LearnSidebar quizzes={poolQuizzes} isActive={isActive} quizIndex={quizIndex} chip="unlock" 
                                        />
                                    </Grid>
                                </Grid>
                            </Container>
                        </Box>
                        <Footer />
                    </>  
            }
        </>
    )
}

export default LearnPage;